/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useState, useEffect, useCallback } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useHistory, useLocation } from 'react-router-dom';
import {
  fidpAnonymous,
  googleCaptchaSiteKey,
  disableGoogleCaptcha,
  fidpE2EUser,
  enableLoginCopilotPrompt,
  enableAsgardeoMaintenanceFlag,
} from 'config';
import { isChrome, isFirefox } from 'utils/browser';
import LoginCard from 'modules/login/LoginCard';
import CopilotLanding from 'modules/login/CopilotLanding';
import type { PrebuiltIntegrationFlow } from 'modules/login/PrebuiltIntegrationFlows';
import AuthDialog, { AuthDialogMode } from 'modules/login/AuthDialog';
import {
  getVSCodePluginParams,
  hasVSCodePluginParams,
} from 'utils/vsCodePlugin/utm';
import PreLoader from 'pages/preloader/PreLoader';
import AuthMainLayout from 'components/ChoreoSystem/Authenticate/AuthMainLayout/AuthMainLayout';
import useSignInFlow from 'hooks/authFlows/useSignInFlow';
import useSignUpFlow from 'hooks/authFlows/useSignUpFlow';
import {
  clearPendingCopilotPrompt,
  getPendingCopilotPrompt,
  setPendingCopilotPrompt,
} from 'utils/copilotPrompt';
import {
  clearPendingExploreIntegrations,
  hasPendingExploreIntegrations,
  setPendingExploreIntegrations,
} from 'utils/exploreIntegrationsIntent';
import {
  clearPendingPrebuiltIntegration,
  setPendingPrebuiltIntegration,
} from 'utils/prebuiltIntegrationIntent';
import { COPILOT_NEW_PATH, EXPLORE_INTEGRATIONS_PATH } from 'utils/route';
import {
  trackLoginCopilotPromptSubmit,
  trackLoginExploreIntegrationsClick,
  trackLoginPrebuiltIntegrationClick,
} from 'utils/tracking';
import { AUTH_METHOD_BASIC } from './constants';

interface LocationState {
  returnToUrl: string;
  returnToOrg: string;
  confirmationKey: string;
  confirmationOrg: string;
  displayOrgName: string;
}

function Login() {
  const history = useHistory();
  const { state, search } = useLocation<LocationState>();
  const returnToOrg = state?.returnToOrg || '';
  const confirmationOrg = state?.confirmationOrg || '';
  const confirmationKey = state?.confirmationKey || '';
  const displayOrgName = state?.displayOrgName || '';
  const isSupportedEngine = isChrome() || isFirefox();
  const [queryParams, setQueryParams] = useState(new URLSearchParams(search));
  const isInvitation = !!confirmationOrg && !!confirmationKey;
  const fidpQueryParam = queryParams.get('fidp');
  const loginMethodQueryParam = queryParams.get('method');
  const [isMobile, setIsMobile] = useState(false);
  const requestedReturnToUrl =
    state?.returnToUrl || queryParams.get('returnToUrl') || '';
  const returnToUrl = requestedReturnToUrl || '/';

  // A return URL only counts as a destination when it points somewhere other
  // than the app root. A plain visit to the site reaches this page through
  // ProtectedRoute, which hands the path it bounced off along — so an ordinary
  // visitor arrives carrying `returnToUrl: '/'`. Read as a deep link, that put
  // the sign in dialog in front of every arrival and stopped the landing page
  // from ever parking a prompt, which is the whole point of the surface.
  // `useNavigateToProtectedPath` draws the same line on the way back in, where
  // a return URL of `/` means "wherever home turns out to be" rather than a
  // page that was asked for.
  const explicitReturnToUrl =
    requestedReturnToUrl.split(/[?#]/)[0] === '/' ? '' : requestedReturnToUrl;

  // ── Which surface to show ─────────────────────────────────────────────
  // The Copilot landing page replaces the sign in card only for a plain visit
  // to /login. An invitation carries its own "you have been invited to X"
  // messaging that the landing page has no place for, so those entries keep the
  // original layout, as does everything else when the flag is off.
  //
  // The VS Code plugin is the third exception. It opens a browser window
  // straight at /login carrying neither a `returnToUrl` nor an `fidp`, so
  // nothing else here would tell it apart from an ordinary visit: the developer
  // would get a hero, a prompt box and a marketing footer inside their IDE's
  // auth window, and would have to find "Log in" in the header to reach a
  // provider button. Signing in would still hand control back to the plugin —
  // `useSignInFlow` reads the same params — but the prompt box has no business
  // being on screen at all, not least because a prompt typed into it would be
  // parked in local storage and never consumed: the plugin's redirect replaces
  // the console's `state` wholesale, so this browser never comes back here.
  const isVSCodePluginSignIn = hasVSCodePluginParams(
    getVSCodePluginParams([...queryParams])
  );
  const showCopilotLanding =
    enableLoginCopilotPrompt && !isInvitation && !isVSCodePluginSignIn;

  // ── Copilot prompt hand-over ──────────────────────────────────────────
  // A prompt is parked in local storage the moment it is submitted, before the
  // user picks an identity provider, because signing in leaves the page
  // entirely. `hasPendingPrompt` mirrors it in state so the return URL below
  // switches over in the same render.
  const [hasPendingPrompt, setHasPendingPrompt] = useState(
    () => !!getPendingCopilotPrompt()
  );

  // Explore integrations needs no store to survive the redirect: the sentinel
  // below rides along in the sign in call, which is rebuilt on the render this
  // triggers, before any provider button can be pressed. The parked copy in
  // local storage is only for a brand new organization, whose landing page
  // CreateDefaultEnvs decides without ever seeing a return URL. It is seeded
  // from that copy for the same reason the prompt is: an intent abandoned by
  // anything other than closing the dialog — the browser Back button, say —
  // outlives the page, and coming back to /login has to be able to revoke it.
  const [wantsExploreIntegrations, setWantsExploreIntegrations] = useState(() =>
    hasPendingExploreIntegrations()
  );

  // With a prompt waiting, sign in should land on the create integration page
  // of the organization's Default project rather than the usual home page, and
  // Explore integrations one page further in. The organization is unknown here,
  // hence the sentinels. The intent expressed last wins: a typed prompt beats a
  // browse pressed before it, because parking the prompt comes after, and an
  // entry point pressed after a prompt revokes that prompt where it is pressed.
  //
  // Nothing parked in local storage gets a say once the visit already has a
  // destination of its own. An invitation, a deep link or the VS Code plugin
  // must arrive where it was going, and with the flag off the landing page that
  // sets these markers is not even on screen — a stale key left over from an
  // earlier visit must not steer a sign in that never offered the choice.
  const getIntendedReturnToUrl = () => {
    if (isInvitation || explicitReturnToUrl || !showCopilotLanding)
      return returnToUrl;
    if (hasPendingPrompt) return COPILOT_NEW_PATH;
    if (wantsExploreIntegrations) return EXPLORE_INTEGRATIONS_PATH;
    return returnToUrl;
  };
  const effectiveReturnToUrl = getIntendedReturnToUrl();

  const {
    isLoading,
    recaptchaRef,
    handleSignIn,
    handleEmailLogin,
    handleGithubLogin,
    handleGoogleLogin,
    handleMicrosoftLogin,
    handleEnterpriseLogin,
    handleAnonymousLogin,
    handleCaptchaChange,
  } = useSignInFlow({
    returnToUrl: effectiveReturnToUrl,
    returnToOrg,
    isInvitation,
    confirmationOrg,
    confirmationKey,
    queryParams,
  });

  const {
    isLoading: isSignUpLoading,
    handleEmailSignup,
    handleGithubSignup,
    handleGoogleSignup,
    handleMicrosoftSignup,
  } = useSignUpFlow({
    returnToUrl: effectiveReturnToUrl,
    returnToOrg,
    queryParams,
  });

  // A plain visit to /login gets the landing page with nothing over it: the
  // dialog waits for a deliberate press — the prompt box, Explore integrations,
  // or Log in / Sign up in the header.
  //
  // Two entries do open it on arrival. `signin=1` is a caller explicitly asking
  // for the dialog rather than the landing page — organization registration
  // bouncing back to authenticate. And a return URL naming a page means the
  // visitor was already on their way somewhere — a deep link, or a bounce off
  // a protected route — so the sign in card goes in front of them rather than
  // a prompt box whose sentence that destination would only outrank. The bare
  // `/` that every ordinary visit carries is not such a page; see
  // `explicitReturnToUrl` above.
  //
  // The landing page still renders behind it. The fidp and method=basic entries
  // never get this far: each short circuits to the preloader while its redirect
  // is set up. The VS Code plugin does reach this point — it names no provider,
  // so there is nothing to redirect to yet — but it is served the sign in card
  // rather than the landing page; see `showCopilotLanding` above.
  const shouldOpenDialogOnMount =
    queryParams.get('signin') === '1' || !!explicitReturnToUrl;

  const [dialogMode, setDialogMode] = useState<AuthDialogMode | null>(
    shouldOpenDialogOnMount ? 'signIn' : null
  );

  // ── Registration maintenance gate ─────────────────────────────────────
  // While registrations are paused, /signup short circuits to the maintenance
  // banner and /account-register redirects to /signup. The dialog's sign up
  // card would otherwise register users regardless, so the sign up mode is
  // closed off here: it can never be entered, and every entry point that asks
  // for it goes to /signup instead — exactly where the "Sign up" link on the
  // sign in card went before this landing page existed, banner and all.
  const openAuthDialog = (mode: AuthDialogMode) => {
    if (mode === 'signUp' && enableAsgardeoMaintenanceFlag) {
      history.push('/signup', state);
      return;
    }
    setDialogMode(mode);
  };

  // Closing the dialog abandons the intent outright. Both markers are revoked,
  // so an abandoned prompt is not replayed on a later sign in on this device
  // and a dropped Explore integrations press does not hijack the destination,
  // and both flags reset so the return URL reverts in this same render.
  const handleAuthDialogClose = () => {
    setDialogMode(null);
    clearPendingCopilotPrompt();
    setHasPendingPrompt(false);
    clearPendingExploreIntegrations();
    setWantsExploreIntegrations(false);
    clearPendingPrebuiltIntegration();
  };

  const handlePromptSubmit = (prompt: string) => {
    // Nothing is stored once the visit has a destination of its own: the prompt
    // could never be handed over from there — see `getIntendedReturnToUrl` —
    // and a copy left in storage would be picked up by whichever create page
    // the user happened to open next, half an hour after watching this one be
    // ignored. It also keeps the dialog's "we've saved your prompt" receipt
    // honest, since it is this flag the receipt is shown from.
    if (!explicitReturnToUrl) {
      setPendingCopilotPrompt(prompt);
      setHasPendingPrompt(true);
    }
    trackLoginCopilotPromptSubmit(prompt.length);
    // The prompt is the sign in prompt: the user has said what they want to
    // build, and an account is what stands between them and building it.
    setDialogMode('signIn');
  };

  const handleExploreIntegrationsClick = () => {
    // Browsing the catalogue is a fresh start, so any integration picked from
    // the row below the prompt and then abandoned is dropped here rather than
    // left to preselect itself on the page this is on its way to. A prompt
    // parked on an earlier visit and never closed out goes the same way: it
    // would otherwise outrank this press and send sign in to the AI builder.
    clearPendingPrebuiltIntegration();
    clearPendingCopilotPrompt();
    setHasPendingPrompt(false);
    setPendingExploreIntegrations();
    setWantsExploreIntegrations(true);
    trackLoginExploreIntegrationsClick();
    setDialogMode('signIn');
  };

  // Picking a pair is the same journey as Explore integrations — the prebuilt
  // integrations catalogue of the Default project — with the first two steps
  // of it already answered. It rides on that intent rather than on one of its
  // own so every redirect between here and there, the brand new organization
  // case included, keeps working untouched; only the pair is parked
  // separately, for the catalogue itself to pick up.
  //
  // Wrapped in `useCallback` so the flows row, which is memoised, is not
  // re-rendered every time this page is. Everything it touches is a module
  // level function or a state setter, so it never needs to change.
  const handlePrebuiltIntegrationClick = useCallback(
    (flow: PrebuiltIntegrationFlow) => {
      // Pressing a pair is a change of mind about anything typed earlier, so a
      // prompt parked on an earlier visit and never closed out is revoked here.
      // Left standing it would outrank this press and land sign in on the AI
      // builder with an abandoned sentence, receipt and all.
      clearPendingCopilotPrompt();
      setHasPendingPrompt(false);
      setPendingPrebuiltIntegration(flow.applications);
      setPendingExploreIntegrations();
      setWantsExploreIntegrations(true);
      trackLoginPrebuiltIntegrationClick(flow.applications);
      // The same sign in card the prompt box and Explore integrations open, with
      // its own "new here? sign up" way across: a visitor pressing a flow has
      // said what they want to build, and an account is what stands between them
      // and building it.
      setDialogMode('signIn');
    },
    []
  );

  useEffect(() => {
    const vsCodePluginParams = getVSCodePluginParams([...queryParams]);

    if (
      vsCodePluginParams.profile === 'vs-code' &&
      vsCodePluginParams.fidp === 'choreoe2etest'
    ) {
      const fidpParam = fidpE2EUser;
      const stateParamObj = {
        fidpId: fidpParam,
      };
      handleSignIn(fidpParam, stateParamObj);
    }
  }, []);

  useEffect(() => {
    const qParams = new URLSearchParams(search);
    setQueryParams(qParams);
  }, [search]);

  useEffect(() => {
    if (loginMethodQueryParam === AUTH_METHOD_BASIC) {
      handleEmailLogin();
    }
  }, [loginMethodQueryParam]);

  useEffect(() => {
    if (fidpQueryParam) {
      if (fidpQueryParam === fidpAnonymous) {
        handleAnonymousLogin();
      } else {
        const stateParamObj = {
          returnToUrl: effectiveReturnToUrl,
        };
        handleSignIn(fidpQueryParam, stateParamObj);
      }
    }
  }, []);

  const handleResize = () => {
    if (window.innerWidth < 1000) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    // Measure once on mount as well: the dialog decides whether to show the
    // logo from this, and it would otherwise stay wrong until a resize.
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const recaptcha = (
    <ReCAPTCHA
      ref={recaptchaRef}
      sitekey={googleCaptchaSiteKey}
      size="invisible"
      onChange={handleCaptchaChange}
    />
  );

  if (isLoading || isSignUpLoading || fidpQueryParam) {
    return (
      <>
        <PreLoader />
        {disableGoogleCaptcha === 'false' &&
          fidpQueryParam === fidpAnonymous &&
          recaptcha}
      </>
    );
  }

  if (showCopilotLanding) {
    return (
      <>
        <CopilotLanding
          onPromptSubmit={handlePromptSubmit}
          onLoginClick={() => openAuthDialog('signIn')}
          onSignUpClick={() => openAuthDialog('signUp')}
          onExploreIntegrationsClick={handleExploreIntegrationsClick}
          onPrebuiltIntegrationClick={handlePrebuiltIntegrationClick}
          isSupportedEngine={isSupportedEngine}
        />

        <AuthDialog
          open={dialogMode !== null}
          // The fallback only stands in for the closed dialog, which has no
          // mode of its own; it is never what a closing dialog shows, since
          // the dialog holds the card it had while it fades out. Sign in is
          // also the only mode on offer while registrations are paused —
          // `openAuthDialog` never stores 'signUp' then, and this backstops it.
          mode={
            enableAsgardeoMaintenanceFlag ? 'signIn' : dialogMode ?? 'signIn'
          }
          onModeChange={openAuthDialog}
          onClose={handleAuthDialogClose}
          isMobile={isMobile}
          // The same flag the return URL above switches on, and it is only ever
          // raised when nothing outranks the prompt, so the receipt the dialog
          // shows and the page sign in actually lands on cannot disagree.
          hasPendingPrompt={hasPendingPrompt}
          onEmailClick={handleEmailLogin}
          onGithubClick={handleGithubLogin}
          onGoogleClick={handleGoogleLogin}
          onMicrosoftClick={handleMicrosoftLogin}
          onAnonymousClick={handleAnonymousLogin}
          onEnterpriseLoginClick={handleEnterpriseLogin}
          onEmailSignUpClick={handleEmailSignup}
          onGithubSignUpClick={handleGithubSignup}
          onGoogleSignUpClick={handleGoogleSignup}
          onMicrosoftSignUpClick={handleMicrosoftSignup}
        />

        {/*
          Rendered outside the dialog: the invisible reCAPTCHA that anonymous
          sign in triggers has to stay mounted while the dialog is open, and
          through the redirect that follows.
        */}
        {disableGoogleCaptcha === 'false' && recaptcha}
      </>
    );
  }

  return (
    <AuthMainLayout isMobile={isMobile} isSupportedEngine={isSupportedEngine}>
      <LoginCard
        isInvitation={isInvitation}
        invitedOrgName={displayOrgName}
        onEmailClick={handleEmailLogin}
        onGithubClick={handleGithubLogin}
        onGoogleClick={handleGoogleLogin}
        onMicrosoftClick={handleMicrosoftLogin}
        onAnonymousClick={handleAnonymousLogin}
        onEnterpriseLoginClick={handleEnterpriseLogin}
        isMobile={isMobile}
      />

      {disableGoogleCaptcha === 'false' && recaptcha}
    </AuthMainLayout>
  );
}

export default Login;
